import { ANALYTICS_PROPERTY, GetListingV3PublicResponse } from '@kouto/types';
import { Sep } from 'components/common/styled/common-styled';
import {
  analyticsManager,
  getAnalyticsDataFromCartItem,
} from 'features/analytics';
import CancellationPolicySummary from 'features/ShoppingCart/components/CancellationPolicySummary';
import { useBrandCurrency } from 'hooks/useBrandCurrency';
import { useCurrencyFormatter } from 'hooks/useCurrencyFormatter';
import useServerCartQuote from 'hooks/useServerCartQuote';
import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { CartItemAddOn, ICartExperience, ICartParticipant } from 'types/cart';
import { CancellationPolicy, Listing } from 'types/listings';
import { currencyFormat } from 'utils';
import { getListingCoverUrl, getListingCoverAltText } from 'utils/listings';
import { useIsMobile } from 'WindowDimensionProvider';
import {
  computeCartItemWithCompoundTaxes,
  Listing as CartListing,
  MinimalCartItem,
} from '@kouto/cart-prices';
import { deriveQuotedItemDetails } from 'utils/cart/mergeServerQuote';
import {
  getStayPricesByTier,
  isContinuousCartItem,
} from 'utils/cart/serverCartQuote';
import {
  adaptListingV3ForCart,
  adaptCartItemForCart,
} from 'utils/data/taxes/taxes';

interface ListingCartItemSummaryProps {
  cartItem: ICartExperience;
  listing?: GetListingV3PublicResponse;
  overriddenParticipants?: ICartParticipant[];
  overriddenAddOns?: CartItemAddOn[];
  cancellationPolicy?: CancellationPolicy;
  isRequestToBookEnabled?: boolean;
}

// Helper to check if listing is V3
const isV3Listing = (
  listing: Listing | GetListingV3PublicResponse,
): listing is GetListingV3PublicResponse => {
  return 'resourceGroups' in listing && Array.isArray(listing.resourceGroups);
};

const ListingCartItemSummary = ({
  cartItem,
  listing,
  overriddenParticipants,
  overriddenAddOns,
  cancellationPolicy,
  isRequestToBookEnabled,
}: ListingCartItemSummaryProps) => {
  const currency = useBrandCurrency();
  const experienceCurrency = currency;

  const isMobile = useIsMobile();
  const { formattedNumber } = useCurrencyFormatter();
  const { t: translate } = useTranslation();

  const currentCartItem = {
    ...cartItem,
    participants:
      overriddenParticipants && overriddenParticipants.length
        ? overriddenParticipants
        : cartItem.participants,
    addOns: overriddenAddOns || cartItem.addOns || [],
  };

  // Convert listing to cart-compatible format
  // eslint-disable-next-line no-nested-ternary
  const cartCompatibleListing: CartListing | undefined = listing
    ? isV3Listing(listing)
      ? adaptListingV3ForCart(listing)
      : listing
    : undefined;

  // Convert cart item's addOns taxes to cart-compatible format
  const cartCompatibleItem = adaptCartItemForCart(currentCartItem);

  // A continuously booked item costs what its length says, which only the server's calculator can
  // work out, so it is quoted here as it is at checkout rather than priced as one base unit.
  const listingKey = currentCartItem.listingId || currentCartItem.experienceId;
  const isContinuous = isContinuousCartItem(currentCartItem, listing);
  const quoteCartItems = useMemo(
    () =>
      isContinuous ? { [currentCartItem.cartItemId]: currentCartItem } : {},
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isContinuous, JSON.stringify(currentCartItem)],
  );
  const quoteListings = useMemo(
    () => (listing ? { [listingKey]: listing } : {}),
    [listing, listingKey],
  );
  // Without the coupon, like the locally priced receipt: an amount coupon is split across the whole
  // cart, so one item quoted alone would take all of it.
  const { quote } = useServerCartQuote(quoteCartItems, quoteListings, {
    withCoupon: false,
  });
  const quotedLine = quote?.lines[currentCartItem.cartItemId];
  const stayPrices = useMemo(
    () => (quotedLine ? getStayPricesByTier(quotedLine) : undefined),
    [quotedLine],
  );

  const localReceiptItems = computeCartItemWithCompoundTaxes(
    cartCompatibleItem as unknown as MinimalCartItem,
    cartCompatibleListing as CartListing,
    undefined,
    undefined,
    undefined,
    isRequestToBookEnabled,
  );

  const receiptItems = isContinuous
    ? deriveQuotedItemDetails(quotedLine, quoteListings, {
        fallbackTaxName: translate('taxesAndFees'),
      })
    : localReceiptItems;

  const {
    isPrivateBooking,
    selectedPriceTiers,
    includedTaxes,
    nonIncludedTaxes,
    subTotal,
    total,
  } = receiptItems;

  useEffect(() => {
    analyticsManager.setAppData({
      [ANALYTICS_PROPERTY.CurrentCartAmount]: `${subTotal > 0 ? total : 0}`,
    });
  }, [subTotal, total]);

  useEffect(() => {
    analyticsManager.setAppData({
      [ANALYTICS_PROPERTY.TotalTaxAmount]: `${nonIncludedTaxes
        .reduce((sum, tax) => sum + tax.amountFee, 0)
        .toFixed(2)}`,
    });
  }, [nonIncludedTaxes]);

  const coverImageUrl = useMemo(() => {
    return listing ? getListingCoverUrl(listing, isMobile) : '';
  }, [listing, isMobile]);

  const coverAltText = useMemo(() => {
    return listing ? getListingCoverAltText(listing) : null;
  }, [listing]);

  useEffect(() => {
    const data = getAnalyticsDataFromCartItem({
      cartData: {
        ...cartItem,
        addOns: currentCartItem.addOns,
      },
      listing,
      locationName: listing?.location?.name,
      priceByTierName: stayPrices,
    });

    analyticsManager.setAppData(data);
  }, [
    cartItem?.experienceId,
    cartItem.listingId,
    currentCartItem.addOns,
    listing?.category?.name,
    listing?.title,
    listing?.location?.name,
    selectedPriceTiers,
    stayPrices,
  ]);

  const shouldShowPrice = !listing?.hidePrice;

  return (
    <Wrapper>
      <SummaryWrapper className="summary-wrapper listing-cart-summary__summary-wrapper">
        <CoverImage
          className="resource-group-summary__resource-image listing-cart-summary__resource-image"
          src={coverImageUrl}
          alt={coverAltText ?? listing?.title ?? ''}
          data-testid="picture-component"
        />

        <Prices isRequestToBookEnabled={isRequestToBookEnabled}>
          <Sep />
          {isRequestToBookEnabled ? null : (
            <>
              {selectedPriceTiers.map((tier) => {
                if (!tier.selectedNumber) {
                  return null;
                }
                return (
                  <PriceRow key={tier.name}>
                    <p className="resource-group-summary__price-tier listing-cart-summary__price-tier">
                      {translate(tier.name)}
                      {!isPrivateBooking && ` (x${tier.selectedNumber})`}
                    </p>
                    {shouldShowPrice && (
                      <p className="resource-group-summary__tier-amount listing-cart-summary__tier-amount">
                        {currencyFormat(experienceCurrency)(
                          formattedNumber(tier.price),
                        )}
                      </p>
                    )}
                  </PriceRow>
                );
              })}
              {currentCartItem.addOns.map((addOn) => {
                if (!addOn.selectedNumber) {
                  return null;
                }
                return (
                  <PriceRow key={addOn.id}>
                    <p className="listing-cart-summary__addon-name">
                      {addOn.label} (x{addOn.selectedNumber})
                    </p>
                    <p className="listing-cart-summary__addon-price">
                      {currencyFormat(experienceCurrency)(
                        formattedNumber(addOn.price),
                      )}
                    </p>
                  </PriceRow>
                );
              })}

              {(shouldShowPrice || currentCartItem.addOns.length > 0) && (
                <Sep style={{ margin: '8px 0px' }} />
              )}
            </>
          )}
          {(shouldShowPrice || subTotal > 0) && (
            <PriceRow>
              <p className="resource-group-summary__total listing-cart-summary__total">
                {isRequestToBookEnabled && currentCartItem?.sessions?.length ? (
                  <>
                    {`${currentCartItem?.sessions?.length - 1} ${translate(
                      'nights',
                    )}`}
                  </>
                ) : (
                  <>
                    {translate('subTotal')}:{' '}
                    {includedTaxes.length > 0 ? '*' : ''}
                  </>
                )}
              </p>
              <p className="resource-group-summary__total-amount listing-cart-summary__total-amount">
                {subTotal > 0
                  ? currencyFormat(experienceCurrency)(
                      formattedNumber(subTotal),
                    )
                  : '-'}
              </p>
            </PriceRow>
          )}
          {nonIncludedTaxes.map((tax) => (
            <PriceRow key={tax.name}>
              <p className="resource-group-summary__tax listing-cart-summary__tax">
                {tax.name}
              </p>
              <p className="resource-group-summary__tax-amount listing-cart-summary__tax-amount">
                {currencyFormat(experienceCurrency)(
                  formattedNumber(tax.amountFee),
                )}
              </p>
            </PriceRow>
          ))}

          {!isRequestToBookEnabled && (shouldShowPrice || total > 0) && (
            <Sep style={{ margin: '8px 0px' }} />
          )}

          {(shouldShowPrice || (total > 0 && subTotal > 0)) && (
            <PriceRow bold>
              <p className="resource-group-summary__total listing-cart-summary__total">
                {translate('total')}:
              </p>
              <p className="resource-group-summary__total-amount listing-cart-summary__total-amount">
                {total > 0 && subTotal > 0
                  ? currencyFormat(experienceCurrency)(formattedNumber(total))
                  : '-'}
              </p>
            </PriceRow>
          )}
          {includedTaxes.length > 0 && (
            <PriceRow>
              <small>
                * {translate('includes')}{' '}
                {includedTaxes
                  .map((t) => `${t.name} (${t.percentage}%)`)
                  .join(` ${translate('and')} `)}
              </small>
            </PriceRow>
          )}
        </Prices>
      </SummaryWrapper>

      <CancellationPolicySummary cancellationPolicy={cancellationPolicy} />
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  max-width: 350px;
  min-width: 350px;

  & > * {
    width: 100%;
    padding: 24px;
  }

  @media (max-width: 768px) {
    max-width: unset;
    min-width: unset;
  }
`;

const CoverImage = styled.img`
  object-fit: cover;
  user-select: none;
  max-height: 100%;
  object-position: center;
  height: 254px;
  border-radius: var(--way-design-borderRadiusDefault);
  width: 100%;
`;

const SummaryWrapper = styled.div`
  box-shadow: var(--way-design-boxShadow-s);
  flex-shrink: 0;
  height: fit-content;
  border: 0.5px solid var(--way-colors-borderColor);
  background: var(--way-palette-white-10);
  align-self: stretch;
`;

const Prices = styled.div<{ isRequestToBookEnabled?: boolean }>`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${({ isRequestToBookEnabled }) =>
    isRequestToBookEnabled ? '20px' : '12px'};
  padding-top: 24px;
`;

const PriceRow = styled.div<{ bold?: boolean }>`
  /* skip css namespace */
  display: flex;
  width: 100%;
  flex-direction: row;
  justify-content: space-between;
  gap: 24px;

  &:last-child {
    margin-bottom: 0px;
  }

  & > p {
    margin: 0px;
    font-size: 15px;
    font-style: normal;
    font-weight: ${({ bold }) => (bold ? '700' : '100')};
    line-height: normal;
    letter-spacing: 0.014px;
  }
`;

export default ListingCartItemSummary;
